// ==========================================
// timeUtils.js - Time Slot Calculation & Conflict Detection
// ==========================================

/**
 * Converts "HH:MM" string to total minutes since midnight.
 * @param {string} time - Time string in "HH:MM" format.
 * @returns {number} Total minutes.
 */
export function timeToMinutes(time) {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Converts total minutes since midnight to "HH:MM" string.
 * @param {number} minutes - Total minutes since midnight.
 * @returns {string} Time string in "HH:MM" format.
 */
export function minutesToTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Generates all possible time slots for a given business, service duration, and date.
 * Checks for conflicts against existing appointments passed as parameter.
 *
 * @param {object} workingHours - { start: "HH:MM", end: "HH:MM" }
 * @param {number} serviceDuration - Duration of the service in minutes.
 * @param {Array} existingAppointments - Array of appointment objects from API.
 * @returns {Array<{ time: string, available: boolean }>} Array of time slot objects.
 */
export function generateTimeSlots(workingHours, serviceDuration, existingAppointments = []) {
  const startMinutes = timeToMinutes(workingHours.start);
  const endMinutes = timeToMinutes(workingHours.end);
  const slotInterval = 30; // Generate slots every 30 minutes

  const slots = [];

  for (let slotStart = startMinutes; slotStart + serviceDuration <= endMinutes; slotStart += slotInterval) {
    const slotEnd = slotStart + serviceDuration;
    const slotStartTime = minutesToTime(slotStart);
    const slotEndTime = minutesToTime(slotEnd);

    // Check if this slot conflicts with any existing appointment
    const hasConflict = checkConflict(slotStart, slotEnd, existingAppointments);

    slots.push({
      time: slotStartTime,
      endTime: slotEndTime,
      available: !hasConflict,
    });
  }

  return slots;
}

/**
 * Checks if a proposed time range conflicts with any existing appointments.
 *
 * Two time ranges [A_start, A_end) and [B_start, B_end) conflict if:
 *   A_start < B_end AND A_end > B_start
 *
 * @param {number} proposedStart - Proposed start time in minutes.
 * @param {number} proposedEnd - Proposed end time in minutes.
 * @param {Array} existingAppointments - Array of appointment objects.
 * @returns {boolean} True if there is a conflict.
 */
export function checkConflict(proposedStart, proposedEnd, existingAppointments) {
  return existingAppointments.some((apt) => {
    const aptStart = timeToMinutes(apt.startTime);
    const aptEnd = timeToMinutes(apt.endTime);
    return proposedStart < aptEnd && proposedEnd > aptStart;
  });
}

/**
 * Formats a date object as "YYYY-MM-DD".
 * @param {Date} date
 * @returns {string}
 */
export function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Returns the Turkish day name for a date.
 * @param {Date} date
 * @returns {string}
 */
export function getDayName(date) {
  const days = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
  return days[date.getDay()];
}

/**
 * Returns the Turkish month name.
 * @param {number} month - 0-indexed month number.
 * @returns {string}
 */
export function getMonthName(month) {
  const months = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
  ];
  return months[month];
}

/**
 * Generates a random 4-digit verification code.
 * @returns {string}
 */
export function generateVerificationCode() {
  return String(Math.floor(1000 + Math.random() * 9000));
}
